import React, { useCallback, useEffect, useState } from "react";
import { Box, Grid, Typography, useMediaQuery } from "@mui/material";
import { sectionWrapperSx } from "./sectionWrapper";
import { useNotify } from "react-admin";
import { settings } from "app";
import { studentRequests } from "api/studentApi";
import dataProvider from "server/dataProvider";

const NO_FEE_PLAN_MESSAGE = "Няма определен план за семестриална такса за този студент.";

const DetailItem = ({ label, value }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: "block", fontWeight: 600 }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: 500, color: value ? "text.primary" : "text.secondary" }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

const formatEuro = amount =>
  `${amount.toLocaleString("bg-BG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const SemesterFee = ({ educationId }) => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));
  const notify = useNotify();
  const [result, setResult] = useState(null);
  const [isPlanMissing, setIsPlanMissing] = useState(false);

  const fetchSemesterFee = useCallback(async () => {
    if (!educationId) return;
    try {
      const { data } = await dataProvider.customRequest(
        studentRequests.getStudentEducationSemesterFees(educationId),
        null,
        "GET",
      );

      setIsPlanMissing(false);
      setResult(data);
    } catch (err) {
      setResult(null);
      // The backend 404s when the student has no annual fee plan.
      setIsPlanMissing(err?.status === 404);
      if (err?.status !== 404)
        notify("Семестриалната такса не можа да бъде заредена. Опитайте да презаредите страницата", {
          type: "error",
        });
    }
  }, [educationId, notify]);

  useEffect(() => {
    fetchSemesterFee();
  }, [fetchSemesterFee]);

  const price = result?.fee?.price;
  const appliedPreferenceId = result?.appliedPreferenceId;

  const appliedDiscount = appliedPreferenceId
    ? result?.matchingPreferenceDiscounts?.find(d => d.preferenceId === appliedPreferenceId)
    : null;

  const percentage = appliedDiscount?.percentage ?? 0;
  // The backend returns the price already discounted.
  const feeText = result?.fee?.isFree || !price?.hasEuroAmount ? "Безплатна" : formatEuro(price.euro);

  if (isPlanMissing) {
    return (
      <Box sx={sectionWrapperSx}>
        <Grid
          container
          sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
        >
          <Grid
            item
            xs={12}
            sx={{ p: 2 }}
          >
            <Typography
              variant="body2"
              color="text.primary"
            >
              {NO_FEE_PLAN_MESSAGE}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (!result) return null;

  return (
    <Box sx={sectionWrapperSx}>
      <Grid
        container
        spacing={2}
        sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
      >
        <Grid
          item
          xs={12}
          md={6}
        >
          <DetailItem
            label="Семестриална такса"
            value={feeText}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
        >
          <DetailItem
            label="Отстъпка"
            value={appliedDiscount ? `${percentage}%` : null}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default SemesterFee;
